import { useQuery } from '@tanstack/react-query';
import { getMediaSource, resolveMediaUrls, SIGNED_LINK_SECONDS } from '../../shared/media';
import { supabase } from '../../shared/supabase/client';

/**
 * The slot a demonstration animation goes in, and what is there when it is not.
 *
 * The answer to the second half is **nothing** — no grey box, no spinner, no
 * caption promising a picture that never arrives. An exercise with no media
 * looks like an exercise, because for most of this product's life that is what
 * an exercise will be: the media is borrowed, dated, and scheduled to go
 * (design C5).
 *
 * Three different causes take the same branch, deliberately:
 *
 *   * the configuration row says `enabled = false`;
 *   * the exercise has no media key;
 *   * the store has no object for that key, or no longer exists at all.
 *
 * They are indistinguishable on screen, which is what makes switching the media
 * off a configuration change rather than a user-interface change made under
 * time pressure on the day it has to happen.
 */

export interface ExerciseMediaSet {
  /** Signed link by media key. Empty whenever there is no media to show. */
  urls: Map<string, string>;
  /** Whose media this is, in their words. Shown wherever a frame is shown. */
  attribution: string | null;
}

const NONE: ExerciseMediaSet = { urls: new Map(), attribution: null };

/**
 * Signed links for every exercise a page is about to show — **one call**, not
 * one per exercise.
 *
 * The configuration row is read at runtime and cached briefly, so that
 * switching the media off takes effect on the next page a person opens rather
 * than on the next deployment.
 */
export function useExerciseMedia(mediaKeys: readonly (string | null)[]): ExerciseMediaSet {
  const source = useQuery({
    queryKey: ['media-source'],
    queryFn: () => getMediaSource(supabase),
    staleTime: 60_000,
  });

  const keys = [...new Set(mediaKeys.filter((key): key is string => key !== null && key !== ''))]
    .sort()
    .join(',');
  const enabled = source.data?.enabled === true && keys !== '';

  const links = useQuery({
    queryKey: ['exercise-media', source.data?.bucket ?? '', keys],
    queryFn: () => resolveMediaUrls(supabase, source.data ?? null, keys.split(',')),
    enabled,
    // Re-mint a few minutes before the hour is up rather than after, so a page
    // left open over lunch does not show a broken frame.
    staleTime: (SIGNED_LINK_SECONDS - 300) * 1000,
  });

  if (!enabled || !links.data) return NONE;
  return { urls: links.data, attribution: source.data?.attribution_note ?? null };
}

/**
 * One exercise's demonstration, or nothing.
 *
 * Nothing here offers the file to the viewer: no link around it, no download
 * attribute, no "open in new tab" affordance, and dragging it out is refused.
 * That is a condition of the arrangement under which this media is borrowed at
 * all, not a design preference.
 *
 * The attribution travels with the frame, every time it is shown, because the
 * rights holder's terms require exactly that.
 */
export function ExerciseMedia({
  media,
  mediaKey,
  name,
  className,
}: {
  media: ExerciseMediaSet;
  mediaKey: string | null;
  name: string;
  className?: string;
}) {
  const url = mediaKey === null ? undefined : media.urls.get(mediaKey);
  if (url === undefined) return null;

  return (
    <figure className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <img
        src={url}
        alt={`Demonstration of ${name}`}
        width={180}
        height={180}
        draggable={false}
        loading="lazy"
        className="bg-surface-muted rounded-[--radius-control] select-none"
      />
      {media.attribution === null ? null : (
        <figcaption className="text-ink-muted text-[0.625rem] leading-tight">
          {media.attribution}
        </figcaption>
      )}
    </figure>
  );
}
